import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * **THE ONE INFO AFFORDANCE IN THIS APP.**
 *
 * Jesse, 2026-08-13, reading the real screens: *"too much explanation… don't
 * need explanations on clients view… **if this description needs to be
 * available make it in (i) icon info popups**."*
 *
 * Nine rounds of critics rewarded "state the reconciliation on screen, in
 * prose", and nothing measured the cost — so the money screens filled with
 * paragraphs that explain figures nobody was misreading. The knowledge was
 * right; the placement was wrong. The rule now:
 *
 * > **The screen carries the FIGURE and a short label. The EXPLANATION lives
 * > behind an (i).** Nothing is deleted — it moves.
 *
 * A figure that is genuinely ambiguous keeps ONE short clause on screen (the
 * qualifier a reader needs to not misread the number — "collected", "not
 * cash", "cash received"). What moves in here is the *paragraph*: the
 * reconciliation, the arithmetic, the worked example, the cross-screen
 * comparison. **A label, not a lesson.**
 *
 * Why a component and not a `title` attribute — the pattern this replaces:
 *  - a native `title` is **unreachable on touch** and invisible to a keyboard
 *    user, so an explanation parked in one is an explanation nobody reads on a
 *    phone. Half this app is read on a phone.
 *  - it cannot hold a link, a figure with `tabular-nums`, or a `<dl>`, and the
 *    explanations being moved are frequently all three.
 *  - three hand-rolled `ⓘ` spans had drifted into three files with three
 *    behaviours. There is one now; converge on it rather than forking it.
 *
 * Behaviour, all of it deliberate:
 *  - **Click / Enter / Space** toggles. It is a real `<button>`, so it is in
 *    the tab order and announces its state (`aria-expanded`).
 *  - **Escape closes and returns focus to the trigger** — the popover never
 *    strands a keyboard user inside it.
 *  - **A click anywhere else closes it.** Hover does NOT open it: an
 *    explanation that appears while you are reaching for the figure under it is
 *    worse than no explanation.
 *  - **Portalled to `document.body` and positioned `fixed`**, because these sit
 *    inside cards with `overflow-hidden`, sticky table headers and
 *    horizontally-scrolling grids — every one of which clips an absolutely
 *    positioned child.
 *  - **It flips above the trigger** when there is not room below, and clamps to
 *    the viewport with a 12px gutter, so at 390px the panel is always wholly on
 *    screen.
 */

export interface InfoHintProps {
  /**
   * What this (i) explains — "Bonus profit basis", "Cash target". It is the
   * button's accessible name ("About <label>") and the popover's heading, so a
   * screen-reader user hears which figure is being explained before the
   * explanation.
   */
  label: string;
  /** The explanation. Nodes, so it can carry figures, a `<dl>` or a link. */
  children: ReactNode;
  /** Extra classes on the trigger — spacing at the call site, nothing else. */
  className?: string;
  /** Panel edge aligned to the trigger. Default `start` (left edges align). */
  align?: 'start' | 'end';
  /** `sm` (12px glyph, inline after a label) or `md` (14px, standing alone). */
  size?: 'sm' | 'md';
  /** Panel width in px. Default 300; clamped to the viewport at 390px anyway. */
  width?: number;
}

const GUTTER = 12;
const GAP = 6;

export function InfoHint({
  label,
  children,
  className,
  align = 'start',
  size = 'sm',
  width = 300,
}: InfoHintProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const close = useCallback((refocus = false) => {
    setOpen(false);
    setPos(null);
    if (refocus) triggerRef.current?.focus();
  }, []);

  /**
   * Measured, not guessed. The panel is rendered invisible for one frame so its
   * real height decides whether it opens downward or flips up — a constant here
   * would put a four-line explanation half off a phone screen.
   */
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const rect = trigger.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(width, vw - GUTTER * 2);
    const h = panel.offsetHeight;

    let left = align === 'end' ? rect.right - w : rect.left;
    left = Math.min(Math.max(left, GUTTER), Math.max(GUTTER, vw - w - GUTTER));

    const below = rect.bottom + GAP;
    const flip = below + h > vh - GUTTER && rect.top - GAP - h > GUTTER;
    const top = flip ? rect.top - GAP - h : Math.min(below, Math.max(GUTTER, vh - h - GUTTER));

    setPos({ top, left, width: w });
  }, [align, width]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
      }
    };
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    };
    // Capture, so a scroll inside the grid this (i) lives in moves the panel
    // with its trigger instead of leaving it floating over the page.
    const onReflow = () => place();
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, close, place]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`About ${label}`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={(event) => {
          // A row label is often a <Link> and a grid cell is often clickable —
          // the (i) must not navigate or start editing the thing it annotates.
          event.preventDefault();
          event.stopPropagation();
          setOpen((v) => !v);
        }}
        className={cn(
          // -m-1 p-1 keeps the glyph tight against its label while giving the
          // tap target real estate a thumb can hit at 390px.
          '-m-1 inline-flex shrink-0 items-center justify-center rounded p-1 align-middle',
          'text-grey-400 transition-colors hover:text-accent',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
          open && 'text-accent',
          className,
        )}
      >
        <Info className={cn(size === 'md' ? 'h-3.5 w-3.5' : 'h-3 w-3')} aria-hidden />
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={label}
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              width: pos?.width ?? width,
              // Rendered off-view for the measuring frame rather than hidden —
              // `display:none` has no height to measure.
              visibility: pos ? 'visible' : 'hidden',
            }}
            className={cn(
              'fixed z-50 rounded-lg border border-grey-200 bg-white p-3 shadow-lg',
              'text-caption leading-snug text-grey-600',
            )}
          >
            <p className="mb-1.5 text-caption font-bold uppercase tracking-wider text-grey-500">
              {label}
            </p>
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}
